import type { AdsDateRange, InsightsCustomerSegment } from "../types";

/**
 * Ordered the way someone reads a range picker: the periods you're partway
 * through, shortest first, then look-backs from shortest to longest, then all
 * time.
 *
 * Study build: every range except Last 12 months is disabled rather than
 * removed. The test compares sessions to each other, so the window has to be
 * the same for everyone — but a picker showing one option reads as broken, and
 * a participant who opens it should see that the range is fixed rather than
 * missing. Last 12 months is already every consumer's default, so nothing
 * needs repointing.
 */
const STUDY_ONLY_RANGE = {
  disabled: true,
  disabledTooltip: "Not relevant for this test.",
} as const;

export const DATE_RANGE_OPTIONS: {
  value: AdsDateRange;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
}[] = [
  { value: "this-quarter", label: "This quarter", ...STUDY_ONLY_RANGE },
  { value: "this-year", label: "This year", ...STUDY_ONLY_RANGE },
  { value: "quarter", label: "Last quarter", ...STUDY_ONLY_RANGE },
  { value: "6m", label: "Last 6 months", ...STUDY_ONLY_RANGE },
  { value: "12m", label: "Last 12 months" },
  { value: "lifetime", label: "Lifetime", ...STUDY_ONLY_RANGE },
];

/**
 * The cut is by the kind of ad that drove the activity, not by who the customer
 * turned out to be — a new customer ad and a reorder ad are separate campaigns
 * with their own budgets and spend. `triggerLabel` keeps the collapsed chip
 * short ("Ad type: New customer").
 *
 * The values still read `*-customers` because `InsightsCustomerSegment` is the
 * shape the data layer already segments by.
 */
export const AD_TYPE_OPTIONS: {
  value: InsightsCustomerSegment;
  label: string;
  triggerLabel: string;
}[] = [
  { value: "all-customers", label: "All ad types", triggerLabel: "All" },
  {
    value: "new-customers",
    label: "New customer ads",
    triggerLabel: "New customer",
  },
  {
    value: "returning-customers",
    label: "Reorder ads",
    triggerLabel: "Reorder",
  },
];

/**
 * Share of a trailing-12-month total each range covers, for the mock cuts that
 * hold one period's totals rather than a month-by-month series. Anchored at 1
 * for "12m", so lifetime runs above it and the shorter windows below. The two
 * current-period ranges are sized to a partial quarter and roughly half a year,
 * which is where they sit on the prototype's July anchor.
 */
export const DATE_RANGE_SCALE: Record<AdsDateRange, number> = {
  "this-quarter": 0.09,
  "this-year": 0.55,
  quarter: 0.28,
  "6m": 0.51,
  "12m": 1,
  lifetime: 1.42,
};

/**
 * The above expressed as a share of the whole program, so a cut whose totals are
 * already lifetime figures can be scaled down to a range without lifetime itself
 * moving.
 */
export function lifetimeShareForRange(range: AdsDateRange): number {
  return DATE_RANGE_SCALE[range] / DATE_RANGE_SCALE.lifetime;
}

/**
 * Inclusive month-index window for a range, anchored to the newest month the
 * advertiser has (which is in progress). Every range runs up to and including
 * that partial month except "Last quarter", which is the previous complete
 * calendar quarter and so is the one range that stops short of it.
 *
 * A window rather than a row count, so a range covers the months it names even
 * if the advertiser has a gap in the middle of their history. Lifetime has no
 * bound and returns null.
 */
export function monthWindowForRange(
  range: AdsDateRange,
  currentMonthIndex: number
): { from: number; to: number } | null {
  // Month indices count from a year boundary, so the remainder mod 12 is the
  // month of the year and mod 3 of that is the months elapsed in the calendar
  // quarter — the in-progress month included in both.
  const monthsIntoYear = currentMonthIndex % 12;
  const thisQuarterStart = currentMonthIndex - (monthsIntoYear % 3);
  const thisYearStart = currentMonthIndex - monthsIntoYear;

  switch (range) {
    case "this-quarter":
      return { from: thisQuarterStart, to: currentMonthIndex };
    case "this-year":
      return { from: thisYearStart, to: currentMonthIndex };
    case "quarter":
      return { from: thisQuarterStart - 3, to: thisQuarterStart - 1 };
    case "6m":
      return { from: currentMonthIndex - 5, to: currentMonthIndex };
    case "12m":
      return { from: currentMonthIndex - 11, to: currentMonthIndex };
    case "lifetime":
      return null;
  }
}
